import { useQuery } from '@tanstack/react-query';
import { FC, memo, useContext, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import styled from 'styled-components';
import { PageKeys } from '../../App.types';
import { PageContext } from '../../context/context';
import { fetchPost } from '../../services/fetchFunc';
import Card from '../UI/Card/Card';
import CustomErrorMessage from '../UI/CustomErrorMessage/CustomErrorMessage';

export interface IAppointment {
    schedid: number;
    patient: string;
    doctor: string;
    filial: string;
    date: string;
    time: string;
    dcode: number;
    filcode: number;
    pcode: number;
    birthday: string;
    title: string;
    price: number;
    ext_avatar: string;
    ext_speciality: string;
    doctor_uid: string;
    status: 'reserved' | 'moving' | 'cancelled';
}

const Appointment: FC = () => {
    const { setPage, setData, data, setErrorMessage } = useContext(PageContext);
    const cancelAppointmentSession = window.sessionStorage.getItem(
        'cancelAppointmentSession',
    );

    const id = new URLSearchParams(window.location.search).get('id');

    type AppointmentResponse = {
        appointment: IAppointment;
        error?: { message: string };
    };
    const hasId = Boolean(id);
    const redirectedNoIdRef = useRef(false);
    const redirectedErrorRef = useRef(false);
    const {
        isLoading,
        isError,
        data: appData,
    } = useQuery<AppointmentResponse, Error>({
        queryKey: ['get-appointment', cancelAppointmentSession, id],
        queryFn: async () => {
            const result = (await fetchPost({
                action: 'get-appointment',
                // session: cancelAppointmentSession,
                id: id,
            })) as AppointmentResponse;

            setData(result.appointment);
            return result;
        },
        enabled: hasId,
        retry: false,
    });

    if (!hasId && !redirectedNoIdRef.current) {
        redirectedNoIdRef.current = true;
        Promise.resolve().then(() => {
            setErrorMessage('Не корректная ссылка');
            setPage(PageKeys.error);
            setData({} as IAppointment);
        });
    }

    if ((isError || appData?.error) && !redirectedErrorRef.current) {
        redirectedErrorRef.current = true;
        Promise.resolve().then(() => {
            setErrorMessage(appData?.error?.message as string);
            setPage(PageKeys.error);
            setData({} as IAppointment);
        });
    }

    const appointment = data;
    const hasAppointment = Boolean(
        appointment && Object.keys(appointment).length > 0,
    );

    return (
        <StyledAppointments>
            {isLoading ? (
                <StyledAppointmentsCardWrapper>
                    <div>Загрузка данных ...</div>
                </StyledAppointmentsCardWrapper>
            ) : (
                <StyledAppointmentsCardWrapper>
                    {hasAppointment ? (
                        <ErrorBoundary
                            key={appointment!.schedid}
                            fallback={
                                <CustomErrorMessage
                                    message={'Компонет поврежден'}
                                />
                            }
                        >
                            <Card {...appointment!} />
                        </ErrorBoundary>
                    ) : (
                        <StyledAppointmentsNotFound>
                            Данные не найдены или ссылка устарела
                        </StyledAppointmentsNotFound>
                    )}
                </StyledAppointmentsCardWrapper>
            )}
        </StyledAppointments>
    );
};

const StyledAppointmentsNotFound = memo(styled.div`
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 160%;
    text-align: center;
    color: #172945;
    margin-top: 76px;
    @media (max-width: 667px) {
        font-size: 14px;
        margin-top: 40px;
    }
`);

const StyledAppointmentsCardWrapper = memo(styled.div`
    animation: fade 0.3s linear;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 60px;
    margin-top: 76px;
    width: 100%;
    @media (max-width: 667px) {
        gap: 32px;
        margin-top: 40px;
    }
`);

const StyledAppointments = memo(styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`);

export default memo(Appointment);
